import React, { useEffect, useState } from 'react';
import { OtherAccess } from './AccessList';
import { NodeMark } from './faces';
import Segmented from './Segmented';
import { FindBar } from './FacetRail';
import { plural, fmt } from '../../nucleus/format';
import '../grant/tokens.css';
import '../grant/parts.css';
import '../grant/shell-align.css';

// THE CONSENT PAGE'S PARTS, IN AN INSPECTOR. A privacy realm and an exposure
// profile are read here with the same components, in the same type and rules, as
// the consent page that made them (components/grant): its realm list, its "How you
// appear", its terms, its data-type rows. `ConsentFrame` is the consent page's
// block (`.gp-route`) laid flat into the panel, so those parts look as they do there.

export function ConsentFrame({ children }) {
    return <div className="gp gp-route nx-consent">{children}</div>;
}

const TWO_TABS = [['rules', 'Permission rules'], ['preview', 'Preview']];

/** Permission rules | Preview (| Used by): the segmented strip, across the frame. */
export function ViewTabs({ value, onChange, tabs = TWO_TABS }) {
    return (
        <div className="nx-consent__tabs">
            <Segmented label="What to show" fill value={value} onChange={onChange} options={tabs.map(([id, label]) => ({ id, label }))} />
        </div>
    );
}

/** The consent page's terms row: label and value pairs, side by side. */
export function TermsRow({ items }) {
    const list = items.filter(Boolean);
    return (
        <div className="gp-terms-row">
            {list.map((t) => (
                <div key={t.label} className="gp-term">
                    <span className="gp-term__k">{t.label}</span>
                    <span className={`gp-term__v${t.tone ? ` is-${t.tone}` : ''}`}>{t.value}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * WHAT THEY CAN SEE. Two parts, as an identity has: the identity graph, node by node
 * with a search, then the other access (plugins, the activity log, automations).
 * `load(q)` answers with the backend's read-back (authz/preview.py), the most
 * recently changed nodes each with only the fields the grant lets through; `access`
 * is the grant's blob, for the other access. `dead` replaces everything when the
 * grant sees nothing (revoked, expired, waiting); `idle` is what is said when the
 * read-back itself is not live. A paused grant read by its owner comes back
 * `as_if_resumed`: what it would show once resumed, under a banner and dimmed.
 * `who` names its grantee there, else "they".
 */
export function PreviewPane({ load, access, dead, idle = 'It sees nothing right now.', who, empty = 'Nothing.' }) {
    const [state, setState] = useState({ loading: true });
    const [q, setQ] = useState('');
    const [asked, setAsked] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setAsked(q.trim()), 220);
        return () => clearTimeout(t);
    }, [q]);
    useEffect(() => {
        let live = true;
        setState((s) => ({ ...s, loading: true }));
        load(asked).then((d) => { if (live) setState({ data: d }); })
            .catch((e) => { if (live) setState({ error: e?.message || 'Could not read it.' }); });
        return () => { live = false; };
    }, [load, asked]);

    if (dead) return <p className="nx-preview__dead">{dead}</p>;
    if (state.error) return <p className="nx-preview__dead">{state.error}</p>;
    const d = state.data;
    if (!d) return <p className="nx-preview__wait">Reading what it can see…</p>;
    const asIf = d.as_if_resumed === true;
    if (d.live === false && !asIf) return <p className="nx-preview__dead">{idle}</p>;
    const shown = d.nodes || [];
    const matched = d.matched ?? d.total;

    return (
        <div className={`nx-preview nx-rules${state.loading ? ' is-loading' : ''}${asIf ? ' is-as-if' : ''}`}>
            {asIf && (
                <p className="nx-preview__asif" role="note">
                    {who ? `Paused: ${who} sees` : 'Paused: they see'} none of this right now. This is what it would show them when you resume it.
                </p>
            )}
            <div className="gp-rcpt__grp nx-preview__head"><span>Identity graph</span><span>{plural(d.total, 'node')}</span></div>
            {d.total > 0 ? (<>
                <FindBar compact className="nx-preview__search" value={q} onChange={setQ} placeholder="Search by title" label="Search what it can see" />
                {shown.length > 0 ? (
                    <ul className="nx-preview__nodes">
                        {shown.map((n) => (
                            <li key={n.id} className="nx-pnode">
                                <div className="nx-pnode__head">
                                    <NodeMark schema={n.schema_name} size={22} />
                                    <span className="nx-pnode__title">{n.title || 'Untitled'}</span>
                                    <span className="nx-pnode__type">{n.schema_name}</span>
                                </div>
                                {(n.fields.length > 0 || n.hidden_fields?.length > 0) && (
                                    <p className="nx-pnode__fields">
                                        {n.fields.map((f) => <span key={f.name}><i>{f.name}</i> {f.value}</span>)}
                                        {n.more_fields > 0 && <span>{plural(n.more_fields, 'more field')}</span>}
                                        {n.hidden_fields?.length > 0 && <span className="is-hidden">hidden: {n.hidden_fields.join(', ')}</span>}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : <p className="nx-preview__dead">Nothing it can see is called that.</p>}
                {shown.length > 0 && shown.length < matched && (
                    <p className="nx-preview__more">The {shown.length} most recently changed, of {fmt(matched)}. Search to find the rest.</p>
                )}
            </>) : <p className="nx-preview__dead">{empty}</p>}
            <OtherAccess access={access} />
        </div>
    );
}
